import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
} from "recharts";

const STATUS_COLORS = {
  in_progress: "#FEA732",
  not_started: "#0060B8",
  on_hold: "#FF6430",
};

const STATUS_LABELS = {
  in_progress: "In Progress",
  not_started: "Not Started",
  on_hold: "On Hold",
};

const renderDueSoonShape = (color, dataKey) => (props) => {
  const { x, y, width, height, payload } = props;
  if (width <= 0 || height <= 0) return null;

  const total = Math.max(0, Math.round(payload?.[dataKey] ?? 0));
  const dueSoonCount = Math.max(0, Math.round(payload?.[`${dataKey}_due_soon`] ?? 0));
  const overdueCount = Math.max(0, Math.round(payload?.[`${dataKey}_overdue`] ?? 0));
  if (total === 0) {
    return <rect x={x} y={y} width={width} height={height} fill={color} />;
  }
  const unitW = width / total;
  const dueSoonPatternId = `stripes-phase-${dataKey}`;
  const overduePatternId = `overdue-phase-${dataKey}`;

  return (
    <g>
      {Array.from({ length: total }).map((_, i) => {
        const isOverdue = i < overdueCount;
        const isDueSoon = !isOverdue && i >= total - dueSoonCount;
        let fill = color;
        if (isOverdue) fill = `url(#${overduePatternId})`;
        else if (isDueSoon) fill = `url(#${dueSoonPatternId})`;
        return (
          <rect
            key={i}
            x={x + i * unitW}
            y={y}
            width={unitW}
            height={height}
            fill={fill}
            stroke="#fff"
            strokeWidth={unitW > 4 ? 1 : 0.5}
          />
        );
      })}
    </g>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div style={{ background: "#fff", border: "1px solid #ddd", padding: "8px 12px", borderRadius: 4, fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.color }}>
          {STATUS_LABELS[p.dataKey] || p.dataKey}: <strong>{p.value}</strong>
          {row?.[`${p.dataKey}_overdue`] > 0 && (
            <span style={{ color: "#d62728", marginLeft: 6, fontWeight: 600 }}>
              ({row[`${p.dataKey}_overdue`]} overdue)
            </span>
          )}
          {row?.[`${p.dataKey}_due_soon`] > 0 && (
            <span style={{ color: "#d62728", marginLeft: 6 }}>
              ({row[`${p.dataKey}_due_soon`]} due ≤2d)
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

const PhaseStackBarChart = ({ data, loading, onSegmentClick }) => {
  const [hidden, setHidden] = useState({});

  if (loading) {
    return <div className="text-center text-muted py-5">Loading phase chart…</div>;
  }
  if (!data || data.length === 0) {
    return <div className="text-center text-muted py-5">No phase data for current filters.</div>;
  }

  const handleClick = (bucket) => (entry) => {
    if (onSegmentClick && entry?.phase_id != null) {
      onSegmentClick({ phase_id: entry.phase_id, bucket });
    }
  };

  const toggleLegend = (e) => {
    const key = e?.dataKey;
    if (!key) return;
    setHidden((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderLegendText = (value, entry) => {
    const isHidden = hidden[entry?.dataKey];
    return (
      <span style={{
        color: isHidden ? "#aaa" : "#333",
        textDecoration: isHidden ? "line-through" : "none",
        cursor: "pointer",
        userSelect: "none",
      }}>
        {value}
      </span>
    );
  };

  const chartHeight = Math.max(180, data.length * 50 + 80);

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart data={data} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
        <defs>
          {Object.entries(STATUS_COLORS).map(([key, color]) => (
            <React.Fragment key={key}>
              <pattern
                id={`stripes-phase-${key}`}
                patternUnits="userSpaceOnUse"
                width="6"
                height="6"
                patternTransform="rotate(45)"
              >
                <rect width="6" height="6" fill={color} />
                <line x1="0" y1="0" x2="0" y2="6" stroke="rgba(255,255,255,0.65)" strokeWidth="2" />
              </pattern>
              <pattern
                id={`overdue-phase-${key}`}
                patternUnits="userSpaceOnUse"
                width="6"
                height="6"
                patternTransform="rotate(45)"
              >
                <rect width="6" height="6" fill={color} />
                <line x1="0" y1="0" x2="0" y2="6" stroke="#d62728" strokeWidth="2.5" />
              </pattern>
            </React.Fragment>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" allowDecimals={false} label={{ value: "No. of Phase Rows", position: "insideBottom", offset: -5 }} />
        <YAxis type="category" dataKey="phase_name" width={140} />
        <Tooltip content={<CustomTooltip />} />
        <Legend onClick={toggleLegend} formatter={renderLegendText} />
        <Bar
          dataKey="in_progress"
          stackId="jobs"
          name={STATUS_LABELS.in_progress}
          fill={STATUS_COLORS.in_progress}
          shape={renderDueSoonShape(STATUS_COLORS.in_progress, "in_progress")}
          onClick={handleClick("in_progress")}
          cursor="pointer"
          hide={!!hidden.in_progress}
        >
          {data.map((_, i) => <Cell key={`ip-${i}`} />)}
        </Bar>
        <Bar
          dataKey="not_started"
          stackId="jobs"
          name={STATUS_LABELS.not_started}
          fill={STATUS_COLORS.not_started}
          shape={renderDueSoonShape(STATUS_COLORS.not_started, "not_started")}
          onClick={handleClick("not_started")}
          cursor="pointer"
          hide={!!hidden.not_started}
        >
          {data.map((_, i) => <Cell key={`ns-${i}`} />)}
        </Bar>
        <Bar
          dataKey="on_hold"
          stackId="jobs"
          name={STATUS_LABELS.on_hold}
          fill={STATUS_COLORS.on_hold}
          shape={renderDueSoonShape(STATUS_COLORS.on_hold, "on_hold")}
          onClick={handleClick("on_hold")}
          cursor="pointer"
          hide={!!hidden.on_hold}
        >
          {data.map((_, i) => <Cell key={`oh-${i}`} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default PhaseStackBarChart;
